import * as React from 'react';
import { getObjectValues, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import DateTimeHover from '../date_time_component/DateTime';
import ProjectStatusDisplay from '../project_status_display/ProjectStatusDisplay';
import { PROJECT_ENTITY_TYPES } from '../../constants/ProjectEntities';
import { isProjectsV2Enabled } from '../../utils/FeatureFlagUtils';
const moment = require('moment');
const style = require('../WorkOrderTable/rowDisplay/RowDisplay.less');

// Shared gray metadata text style used across the row's sub-rows.
const metaStyle: React.CSSProperties = {
	whiteSpace: 'nowrap',
	color: 'rgba(0,0,0,0.45)',
	fontSize: 14,
};

export const ProjectsRowDisplay: React.FC<any> = ({ project }) => {
	// Total across the WO metric buckets. Default to {} and seed the reduce so a
	// project without workOrderProjectMetrics doesn't throw (Object.keys(null) /
	// reduce-of-empty-array).
	const woMetricsTotal = getObjectValues(
		nullSafeGetOrElse('workOrderProjectMetrics', project, {})
	).reduce((sum, value) => sum + value, 0);
	let incompleteNumber =
		woMetricsTotal - nullSafeGetOrElse('workOrderProjectMetrics.workOrdersCompleted', project, 0);
	let budgetApproved = nullSafeGetOrElse(
		'invoiceProjectMetrics.totalInvoicesAmountApproved',
		project,
		0
	);
	let closedNumber = nullSafeGetOrElse('workOrderProjectMetrics.workOrdersClosed', project, 0);
	let budgetSpent = (budgetApproved * 100) / nullSafeGet('budget', project);
	let budgetText = '';
	if (isNaN(budgetSpent)) {
		budgetText = undefined;
	} else {
		if (budgetSpent > 100) {
			budgetText = (budgetSpent - 100).toFixed(2).toString().concat('% Over Budget');
		} else {
			budgetText = (100 - budgetSpent).toFixed(2).toString().concat('% Budget Remaining');
		}
	}
	if (!project) {
		return null;
	}

	// Status pill + deliverables/tasks rollup line are gated on the project's
	// entities (same gate as the Deliverables/Tasks tabs).
	const entities = nullSafeGetOrElse('entities', project, []);
	const hasDeliverables = entities.includes(PROJECT_ENTITY_TYPES.deliverables);
	const hasTasks = entities.includes(PROJECT_ENTITY_TYPES.tasks);
	const status = nullSafeGet('status', project);
	const deliverableMetrics = nullSafeGet('deliverableProjectMetrics', project);
	const deliverablesTotal = deliverableMetrics
		? nullSafeGetOrElse('deliverablesNotStarted', deliverableMetrics, 0) +
		  nullSafeGetOrElse('deliverablesInProgress', deliverableMetrics, 0) +
		  nullSafeGetOrElse('deliverablesCompleted', deliverableMetrics, 0)
		: 0;
	const deliverablesText =
		deliverablesTotal > 0
			? `${nullSafeGetOrElse('deliverablesCompleted', deliverableMetrics, 0)}/${deliverablesTotal}`
			: '--';
	const taskMetrics = nullSafeGet('taskProjectMetrics', project);
	const tasksText = taskMetrics
		? `${nullSafeGetOrElse('tasksCompleted', taskMetrics, 0)}/${nullSafeGetOrElse(
				'tasksTotal',
				taskMetrics,
				0
		  )}`
		: '--';
	// projects-v2 gates the status pill + deliverables/tasks rollup line.
	const projectsV2 = isProjectsV2Enabled();
	const showStatus = projectsV2 && (hasDeliverables || hasTasks);
	const showDeliverablesRow = projectsV2 && (hasDeliverables || hasTasks);

	return (
		<div className="index-padding" style={{ display: 'flex', width: '100%' }}>
			<div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', width: '100%' }}>
				<div style={{ display: 'flex', flexDirection: 'column', flexWrap: 'nowrap' }}>
					{showStatus && status ? (
						<div style={{ marginBottom: 4 }}>
							<ProjectStatusDisplay status={status} />
						</div>
					) : null}
					<div className="workOrderRowDisplay__rowTitle">
						<div
							style={{
								maxWidth: '100%',
								overflowX: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								color: 'rgba(0,0,0,.65)',
							}}
						>
							{project.title}
						</div>
					</div>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							flexWrap: 'nowrap',
							gap: '24px',
							alignItems: 'baseline',
						}}
						className="workOrderRowDisplay__rowInlineGroup"
					>
						{woMetricsTotal > 0 ? (
							[
								<span style={metaStyle}>
									{closedNumber
										.toString()
										.concat(' of ', woMetricsTotal.toString().concat(' Work Orders Closed'))}
								</span>,
								<span style={{ ...metaStyle, display: 'block' }}>{budgetText}</span>,
							]
						) : (
							<span style={metaStyle}>No work orders added</span>
						)}
						<span style={{ ...metaStyle, display: 'block' }}>
							{`Created `}{' '}
							<DateTimeHover timestamp={nullSafeGet('createdAt', project)} showTime={false} />
						</span>
					</div>
					{showDeliverablesRow ? (
						<div
							style={{
								display: 'flex',
								flexDirection: 'row',
								flexWrap: 'nowrap',
								gap: '24px',
								alignItems: 'baseline',
								marginTop: 4,
							}}
						>
							{hasDeliverables ? (
								<span style={metaStyle}>{`${deliverablesText} deliverables completed`}</span>
							) : null}
							{hasTasks ? (
								<span
									style={{ ...metaStyle, display: 'block' }}
								>{`${tasksText} tasks completed`}</span>
							) : null}
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
};
